import React, { ChangeEvent, useContext, useEffect, useMemo } from "react";
import _ from "lodash";
import cx from "classnames";
import { Spring, animated } from "@react-spring/web";
import useMeasure from "react-use-measure";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

import { MenuState } from "../../types";
import { Collection, CollectionBanner } from "../../../../types/collections";
import { MediaFile } from "../../../../types/media";

import Thumbnail from "src/components/thumbnail";
import Actions from "../../../../components/actionbar";
import { CollaborateDialog } from "../collaborate";
import { ChooseExperienceDialog, GenerateLinkDialog, InternalConfirmationDialog, TemplateConfirmationDialog } from "../dialogs";
import ComposeDialog from "src/components/compose/components/dialog";
import { LiveConfirmationDialog } from "../live";

import { CollectionContext } from "../context";

import { invalidateMediaUpload, uploadMedia } from "../../../../redux/actions/media";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";
import { addAssetToCollection } from "src/redux/actions/collections";

import { parseMediaMetadata } from "src/utils/media.service";
import userRoleService from "src/utils/userRoles.service";
import { generateDefaultContactCard, getContactCardExists } from "src/utils/collections.helper";
import { POSITIONS } from "src/constants/banner";
import { BENTO_EXPERIENCE, USER_DEFINED } from "src/constants/collections";

import styles from "./menu.module.scss";
import { addAssetToConversation } from "src/redux/actions/conversation";
const DefaultThumbnail = '/images/default_user_profile_background.png';

interface MenubarProps {
  collection: Collection;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onChange: (value: MenuState) => void;
}

const Menubar: React.FC<MenubarProps> = ({ collection, menuState, onChange }) => {
  const collectionContext = useContext(CollectionContext);

  const { trackBannerFiltered, trackBannerCropped } = useCollectionsAdoption();

  const actionBarRef = React.useRef<HTMLDivElement>(null);

  const persistedCollection = useSelector((state: any) => {
    return _.get(state, `${collectionContext.itemSelector}`);
  });

  const [viewMenuRef, viewMenuBounds] = useMeasure();
  const [editMenuRef, editMenuBounds] = useMeasure();
  const [bannerMenuRef, bannerMenuBounds] = useMeasure();
  const [confirmationMenuRef, confirmationMenuBounds] = useMeasure();
  const [positionMenuRef, positionMenuBounds] = useMeasure();

  const showCrop = menuState.primaryAction === 'edit-banner' && (_.includes(['crop', 'apply-crop'], menuState.secondaryAction));
  const showFilter = menuState.primaryAction === 'edit-banner' && (_.includes(['filter'], menuState.secondaryAction));
  const showConfirmation = showCrop || showFilter;
  const showPosition = menuState.primaryAction === 'edit-banner' && (_.includes(['position'], menuState.secondaryAction));

  const [dimensions, setDimensions] = React.useState<{ width: number | string, height: number | string }>({
    width: 'auto',
    height: 0
  });

  const handleMenuChange = (value) => {
    onChange({
      ...menuState,
      ...value
    });
  }

  const handleConfirmationClick = () => {
    if (showCrop) {
      handleMenuChange({
        ...menuState,
        secondaryAction: 'apply-crop'
      });

      trackBannerCropped(collection);

    } else if (showFilter) {
      handleMenuChange({
        ...menuState,
        secondaryAction: null
      });

      trackBannerFiltered(collection);
    }
  }

  const handleConfirmationCancel = () => {
    if (showCrop) {
      handleMenuChange({
        ...menuState,
        secondaryAction: null
      });
    } else if (showFilter) {
      collectionContext.updateCollectionProperty('banner', {
        ...collection.banner,
        filter: persistedCollection.banner?.filter,
        overlayColor: persistedCollection.banner?.overlayColor
      });

      handleMenuChange({
        ...menuState,
        secondaryAction: null
      });
    }
  }

  useEffect(() => {
    let width = 0;
    let height = 0;
    if (menuState.primaryAction === 'view') {
      width = viewMenuBounds.width || 0;
      height = viewMenuBounds.height || 0;
    } else if (menuState.primaryAction === 'edit') {
      width = editMenuBounds.width || 0;
      height = editMenuBounds.height || 0;
    } else if (menuState.primaryAction === 'edit-banner' && !menuState.secondaryAction) {
      width = bannerMenuBounds.width || 0;
      height = bannerMenuBounds.height || 0;
    } else if (showConfirmation) {
      width = confirmationMenuBounds.width || 0;
      height = confirmationMenuBounds.height || 0;
    } else if (showPosition) {
      width = positionMenuBounds.width || 0;
      height = positionMenuBounds.height || 0;
    }

    setDimensions({
      width: width + 20,
      height: height + 20
    });

  }, [menuState.primaryAction, viewMenuBounds, editMenuBounds, bannerMenuBounds, confirmationMenuBounds, positionMenuBounds, showConfirmation, showPosition]);

  return (
    <Spring to={{ width: dimensions.width, height: dimensions.height }} config={{
      friction: 14,
      tension: 120,
    }}>
      {props => (
        <animated.div ref={actionBarRef} style={props} className={styles.actionBar}>
          <div className={cx(styles.menu, styles.viewMenu, { [styles.show]: menuState.primaryAction === 'view' })}>
            <div ref={viewMenuRef} className={styles.inner}>
              <ViewMenu collection={collection} menuState={menuState} onMenuChange={handleMenuChange} />
            </div>
          </div>

          <div className={cx(styles.menu, styles.editMenu, { [styles.show]: menuState.primaryAction === 'edit' })}>
            <div ref={editMenuRef} className={styles.inner}>
              <EditMenu
                collection={collection}
                menuState={menuState}
                onMenuChange={handleMenuChange}
              />
            </div>
          </div>

          <div className={cx(styles.menu, styles.bannerMenu, { [styles.show]: menuState.primaryAction === 'edit-banner' && !menuState.secondaryAction })}>
            <div ref={bannerMenuRef} className={styles.inner}>
              <EditBannerMenu
                collection={collection}
                menuState={menuState}
                onMenuChange={handleMenuChange}
              />
            </div>
          </div>

          <div className={cx(styles.menu, styles.confirmationMenu, {
            [styles.show]: showConfirmation
          })}>
            <div ref={confirmationMenuRef} className={styles.inner}>
              <ConfirmationMenu onConfirm={handleConfirmationClick} onCancel={handleConfirmationCancel} />
            </div>
          </div>

          <div className={cx(styles.menu, styles.positionMenu, {
            [styles.show]: showPosition
          })}>
            <div ref={positionMenuRef} className={styles.inner}>
              <BannerPositionMenu
                collection={collection}
                menuState={menuState}
                onMenuChange={handleMenuChange}
              />
            </div>
          </div>
        </animated.div>
      )}
    </Spring>
  );
}

interface ViewMenuProps {
  collection: Collection;
  menuState: MenuState;

  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
}
const ViewMenu: React.FC<ViewMenuProps> = ({ collection, menuState, onMenuChange }) => {
  const dispatch = useDispatch();

  const {
    trackEditCollectionClicked,
    trackCollaborateClicked,
    trackLiveCollectionToggleClicked,
    trackTemplateToggleClicked,
    trackInternalUseToggleClicked,
    trackGenerateLinkClicked,
    trackShareCollectionClicked
  } = useCollectionsAdoption();

  const [showGenerateLink, setShowGenerateLink] = React.useState(false);
  const [showInternal, setShowInternal] = React.useState(false);
  const [showTemplate, setShowTemplate] = React.useState(false);
  const [showLive, setShowLive] = React.useState(false);
  const [showCollaborate, setShowCollaborate] = React.useState(false);
  const [showCompose, setShowCompose] = React.useState(false);

  const template = collection.collectionTemplate;
  const internal = collection.internal;
  const live = collection.live;
  const collaborated = collection.readAll || collection.writeAll || (collection?.collaboratorsCount ?? 0) > 0;

  const collaborateTooltip = useMemo(() => {
    if (!collaborated) {
      return 'Collaborate';
    }

    if (collection.writeAll || collection.readAll) {
      return 'Collaborated with all users';
    }

    return `Collaborated with ${collection?.collaboratorsCount} users`;

  }, [collaborated]);

  const collaboratedTooltip = useMemo(() => {
    return `Collaborated by ${_.get(collection, 'createdBy.firstName', "")} ${_.get(collection, 'createdBy.lastName', "")}`;
  }, [collection]);


  const user = useSelector((state: any) => state.users?.me);

  const canEdit = useMemo(() => {
    return collection?.type == USER_DEFINED && collection?.canEdit;
  }, [collection]);

  const canCollaborate = useMemo(() => {
    return collection?.type == USER_DEFINED && collection?.creator && userRoleService.canCollaborateCollection();
  }, [collection]);

  const isCollaborated = useMemo(() => {
    return collection.type == USER_DEFINED && !collection.creator
  }, [collection]);

  const canMarkInternal = useMemo(() => {
    return userRoleService.canShare()
      && collection?.type == USER_DEFINED
      && (user?.admin || collection.creator)
  }, [user, collection]);

  const canSwitchLiveCollection = useMemo(() => {
    return userRoleService.canShare()
      && collection?.type == USER_DEFINED
      && collection?.canEdit
      && !collection?.internal
      && (
        _.size(collection.sections) > 0 ||
        (_.size(collection.entities) > 0 && _.some(collection?.entities, { type: "ASSET" }))
      )
  }, [collection]);

  const canChangeTemplate = useMemo(() => {
    return collection?.type == USER_DEFINED && (
      (user?.admin
        && user?.permissions?.createStream
        && collection?.canEdit) || userRoleService.canMarkAsTemplate())
  }, [collection, user]);

  const canShare = useMemo(() => {
    return collection?.type == USER_DEFINED
      && ((_.size(collection?.entities) > 0 && _.some(collection?.entities, { type: "ASSET" })) || _.size(collection?.sections) > 0)
      && userRoleService.canShare()
      && !collection?.internal
  }, [collection, user]);

  const socialShareEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.socialShareEnabled", false),
  );

  const seekEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.seekEnabled", false),
  );

  const handleEdit = () => {
    onMenuChange({
      ...menuState,
      primaryAction: 'edit'
    });

    trackEditCollectionClicked(collection);
  }

  const handleShowInternal = () => {
    setShowInternal(true);

    trackInternalUseToggleClicked(collection);
  }

  const handleShowInternalClose = () => {
    setShowInternal(false);
  }

  const handleShowTemplate = () => {
    setShowTemplate(true);

    trackTemplateToggleClicked(collection);
  }

  const handleShowTemplateClose = () => {
    setShowTemplate(false);
  }

  const handleShowLive = () => {
    setShowLive(true);

    trackLiveCollectionToggleClicked(collection);
  }

  const handleShowLiveClose = () => {
    setShowLive(false);
  }

  const handleShowGenerateLink = () => {
    setShowGenerateLink(true);
    trackGenerateLinkClicked(collection);
  }

  const handleShowComposeDialog = () => {
    setShowCompose(true);
    trackShareCollectionClicked(collection, 'email');
  }

  const handleAddAsset = () => {
    onMenuChange({
      primaryAction: 'edit',
      secondaryAction: 'add-asset'
    });
  }

  const handleShowCollaborate = () => {
    setShowCollaborate(true);

    trackCollaborateClicked(collection);
  }

  const handleSocialShare = (type) => {
    onMenuChange({
      ...menuState,
      secondaryAction: `social-share`,
      data: type
    });

    trackShareCollectionClicked(collection, type);
  }

  const showMoreActions = canEdit && (canMarkInternal || canSwitchLiveCollection || canCollaborate || canChangeTemplate);

  return (
    <>
      <Actions.Container>
        <Actions.Row>
          {canEdit &&
            <>
              <Actions.IconButton icon="paperflite-edit" tooltip="Edit Collection" onClick={handleEdit} />
              <Actions.Button width={3} icon="paperflite-add" title="Add Assets" onClick={handleAddAsset} />
            </>
          }
          {canShare && socialShareEnabled &&
            <>
              <Actions.Group icon="paperflite-share">
                <Actions.IconButton icon="paperflite-mail" tooltip="Compose" onClick={handleShowComposeDialog} />
                <Actions.IconButton icon="paperflite-facebook" tooltip="Facebook" onClick={() => handleSocialShare('facebook')} />
                <Actions.IconButton icon="paperflite-twitter" tooltip="Twitter" onClick={() => handleSocialShare('twitter')} />
                <Actions.IconButton icon="paperflite-linkedIn" tooltip="Linkedin" onClick={() => handleSocialShare('linkedin')} />
              </Actions.Group>
              <Actions.IconButton icon="paperflite-link" tooltip="Generate Link" onClick={handleShowGenerateLink} />
            </>
          }

          {canShare && !socialShareEnabled && (
            <>
              <Actions.IconButton
                icon="paperflite-share"
                tooltip="Share"
                onClick={handleShowComposeDialog}
              />
              <Actions.IconButton icon="paperflite-link" tooltip="Generate Link" onClick={handleShowGenerateLink} />
            </>
          )}

          {seekEnabled &&
            <Actions.Seek />
          }

          {showMoreActions &&
            <Actions.Group icon="paperflite-more">
              {canMarkInternal &&
                <Actions.IconButton icon={cx(internal ? "paperflite-lock" : "fa-solid fa-lock-open")}
                  tooltip={internal ? "Collection marked as internal" : "Mark as internal"}
                  className={cx({ [styles.activeAction]: internal })}
                  onClick={handleShowInternal}
                />
              }
              {canSwitchLiveCollection &&
                <Actions.IconButton icon="fa-regular fa-circle-dot"
                  tooltip={live ? 'Collection is live' : 'Go Live'}
                  className={cx({ [styles.activeAction]: live })}
                  onClick={handleShowLive}
                />
              }
              {canCollaborate &&
                <Actions.IconButton icon="paperflite-user-settings-property"
                  tooltip={collaborateTooltip}
                  className={cx({ [styles.activeAction]: collaborated })}
                  onClick={handleShowCollaborate}
                />
              }

              {canChangeTemplate &&
                <Actions.IconButton className={cx({ [styles.primaryAction]: template })} icon="paperflite-categories"
                  tooltip={template ? "Unmark as template" : "Mark as template"}
                  onClick={handleShowTemplate}
                />
              }
            </Actions.Group>
          }

          {isCollaborated &&
            <Actions.IconButton icon="paperflite-user-settings-property"
              className={styles.collaborated}
              tooltip={collaboratedTooltip}
              onClick={() => { }}
            />
          }
        </Actions.Row>
      </Actions.Container>

      <TemplateConfirmationDialog show={showTemplate}
        collection={collection}
        onClose={handleShowTemplateClose}
      />

      <InternalConfirmationDialog show={showInternal}
        collection={collection}
        onClose={handleShowInternalClose}
      />

      <LiveConfirmationDialog show={showLive}
        collection={collection}
        onClose={handleShowLiveClose}
      />

      <CollaborateDialog show={showCollaborate}
        collection={collection}
        onClose={() => setShowCollaborate(false)}
      />

      <ComposeDialog
        show={showCompose}
        dialog={true}
        initialContent={[collection]}
        onClose={() => setShowCompose(false)}
      />
      <GenerateLinkDialog show={showGenerateLink} onClose={() => setShowGenerateLink(false)} collection={collection} />
    </>
  );
}

interface EditMenuProps {
  collection: Collection;
  menuState: MenuState;

  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
}

const EditMenu: React.FC<EditMenuProps> = ({ collection, menuState, onMenuChange }) => {
  const dispatch = useDispatch();
  const collectionContext = useContext(CollectionContext);
  const updating = useSelector((state: any) => _.get(state, `${collectionContext.updateSelector}.updating`, false));

  const bentoExperience = collection?.viewerType === BENTO_EXPERIENCE;
  const [changeExperience, setChangeExperience] = React.useState(false);

  const {
    trackAddSectionClicked,
    trackAddLogoClickedEvent,
    trackAddLogoUploaded,
    trackEditBannerClicked,
    trackChangeExperienceClickedEvent,
    trackEditBannerScreenView
  } = useCollectionsAdoption();

  const handleAddAsset = () => {
    onMenuChange({
      primaryAction: 'edit',
      secondaryAction: 'add-asset'
    });
  }

  const handleBannerEdit = () => {
    trackEditBannerClicked(collection);
    trackEditBannerScreenView();

    onMenuChange({
      ...menuState,
      primaryAction: 'edit-banner',
      secondaryAction: null
    });
  }

  const handleAddSection = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'add-section'
    });

    trackAddSectionClicked(collection);
  }

  const handleCustomerLogoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Please upload a file of size less than 2MB");
      return;
    }

    const customerLogo = await parseMediaMetadata(file);

    collectionContext.updateCollectionProperty('customerLogo', customerLogo);

    dispatch(uploadMedia({
      id: customerLogo?.id as string,
      media: {
        ...customerLogo,
        file: file
      } as MediaFile
    }));

    trackAddLogoUploaded(collection.name);
  }

  const handleChangeExperience = () => {
    setChangeExperience(true);

    trackChangeExperienceClickedEvent(collection);
  }

  const handleResetLayout = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'reset-layout'
    });
  }

  const handleRename = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'rename'
    });
  }

  const handleSave = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'save'
    });
  }

  const handleCancel = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'reset'
    });
  }

  return (
    <>
      <Actions.Container>
        <Actions.Row>
          <Actions.FileUpload width={1}
            icon="paperflite-add-image"
            tooltip="Customer Logo"
            accept="image/*"
            disabled={updating}
            onClick={() => trackAddLogoClickedEvent(collection.name)}
            onChange={handleCustomerLogoChange}
          />
          <Actions.Button width={3} icon="paperflite-add" disabled={updating} title="Add Assets" onClick={handleAddAsset} />
          <Actions.IconButton icon="paperflite-rename" disabled={updating} tooltip="Edit Description" onClick={handleRename} />
          <Actions.Button width={4} icon="paperflite-reverse" disabled={updating} title="Change Experience" onClick={handleChangeExperience} />
          <SharedByEditMenu collection={collection} updating={updating} />
        </Actions.Row>
        <Actions.Row>
          <Actions.IconButton icon="paperflite-add-category" disabled={updating} tooltip="Add Section" onClick={handleAddSection} />
          <Actions.Button width={bentoExperience ? 4 : 5} icon="paperflite-edit" disabled={updating} title="Customise the banner" onClick={handleBannerEdit} />
          {bentoExperience &&
            <Actions.IconButton icon="paperflite-reverse-curved" disabled={updating} tooltip="Reset the layout" onClick={handleResetLayout} />
          }
          <Actions.Button width={3}
            icon="paperflite-tick"
            className={styles.primaryAction}
            disabled={updating}
            title={updating ? "Saving..." : "Save Changes"}
            onClick={handleSave}
          />
          <Actions.Button width={2} icon="paperflite-close" disabled={updating} title="Cancel" onClick={handleCancel} />
        </Actions.Row>
      </Actions.Container>
      <ChooseExperienceDialog
        show={changeExperience}
        collection={collection}
        onClose={() => setChangeExperience(false)}
      />
    </>
  );
}

interface EditBannerMenuProps {
  collection: Collection;
  menuState: MenuState;

  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
}

const EditBannerMenu: React.FC<EditBannerMenuProps> = ({ collection, menuState, onMenuChange }) => {
  const dispatch = useDispatch();
  const collectionContext = useContext(CollectionContext);

  const { trackFilterBannerClicked, trackCropBannerClicked, trackAddBannerClicked, trackBannerAdded } = useCollectionsAdoption();

  const originalBanner = useSelector((state: any) => {
    return _.get(state, `${collectionContext.itemSelector}.banner`, null);
  });

  const uploading = useSelector((state: any) => {
    const background = _.get(state, `${collectionContext.editSelector}.banner.background`);
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].loading`, false);
    }

    return false;
  });

  const uploaded = useSelector((state: any) => {
    const background = _.get(state, `${collectionContext.editSelector}.banner.background`);
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].loaded`, false);
    }

    return false;
  });

  const uploadedBackground = useSelector((state: any) => {
    const background = _.get(state, `${collectionContext.editSelector}.banner.background`);
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].item`);
    }

    return false;
  });

  const canCrop = useMemo(() => {
    return collection?.banner?.background?.type === 'image';
  }, [collection]);

  useEffect(() => {
    if (uploaded) {
      let banner = _.get(collection, 'banner');
      const mediaId = _.get(banner, 'background.id');

      banner = {
        ...banner,
        background: uploadedBackground
      } as CollectionBanner;

      collectionContext.updateCollectionProperty('banner', banner);

      if (mediaId) {
        dispatch(invalidateMediaUpload(mediaId));
      }

      onMenuChange({
        ...menuState,
        primaryAction: 'edit'
      });
    }
  }, [uploaded, uploadedBackground, onMenuChange, menuState]);

  const handleBannerFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Please upload a file of size less than 10MB");
      return;
    }

    const backgroundMedia = await parseMediaMetadata(file);

    collectionContext.updateCollectionProperty('banner', {
      ...collection.banner,
      background: backgroundMedia,
      crop: null
    });

    if (backgroundMedia) {
      trackBannerAdded({
        collection: collection,
        media: backgroundMedia
      });
    }
  }

  const handleSaveBanner = async () => {
    const banner = _.get(collection, 'banner');
    const media = _.get(banner, 'background');
    const entityType = _.get(media, 'entityType');

    if (media && entityType === 'media-preview') {
      const mediaFile = media as MediaFile;
      const blob = await fetch(media!.full!).then(r => r.blob());
      const file = new File([blob], 'banner.png', { type: mediaFile.contentType || blob.type });

      dispatch(uploadMedia({
        id: media.id,
        media: {
          ...media,
          file: file
        } as MediaFile
      }));
    } else {
      onMenuChange({
        ...menuState,
        primaryAction: 'edit'
      });
    }
  }

  const handleCancel = () => {
    collectionContext.updateCollectionProperty('banner', originalBanner);

    onMenuChange({
      ...menuState,
      primaryAction: 'edit'
    });
  }

  const handleCrop = () => {
    trackCropBannerClicked(collection);

    onMenuChange({
      ...menuState,
      secondaryAction: 'crop'
    });
  }

  const handleFilter = () => {
    trackFilterBannerClicked(collection);

    onMenuChange({
      ...menuState,
      secondaryAction: 'filter'
    });
  }

  const handlePosition = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'position'
    });
  }

  return (
    <Actions.Container>
      <Actions.Row>
        <Actions.FileUpload width={5}
          icon="paperflite-upload"
          title="Upload from device"
          accept="image/*,video/*"
          disabled={uploading}
          onClick={() => trackAddBannerClicked(collection)}
          onChange={handleBannerFileChange}
        />
        {canCrop &&
          <>
            <Actions.IconButton
              icon="paperflite-crop"
              tooltip="Crop"
              disabled={uploading}
              onClick={handleCrop}
            />
            <Actions.IconButton
              icon="paperflite-focus"
              tooltip="Position"
              disabled={uploading}
              onClick={handlePosition}
            />
          </>
        }

        {!canCrop &&

          <Actions.Button
            icon="paperflite-focus"
            width={2}
            title="Position"
            disabled={uploading}
            onClick={handlePosition}
          />
        }

      </Actions.Row>
      <Actions.Row>
        <Actions.Button width={2}
          icon="paperflite-filter-property"
          title="Filters"
          disabled={uploading}
          onClick={handleFilter}
        />
        <Actions.Button width={3}
          icon="paperflite-tick"
          title={uploading ? 'Saving...' : 'Save Changes'}
          className={styles.primaryAction}
          disabled={uploading}
          onClick={handleSaveBanner}
        />
        <Actions.Button width={2}
          icon="paperflite-close"
          title="Cancel"
          disabled={uploading}
          onClick={handleCancel}
        />
      </Actions.Row>
    </Actions.Container>
  );
}

interface ConfirmationMenuProps {
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationMenu: React.FC<ConfirmationMenuProps> = ({ onConfirm, onCancel }) => {
  return (
    <Actions.Container>
      <Actions.Row>
        <Actions.Button width={3} icon="paperflite-tick" className={styles.primaryAction} title="Done" onClick={onConfirm} />
        <Actions.Button width={3} icon="paperflite-close" title="Cancel" onClick={onCancel} />
      </Actions.Row>
    </Actions.Container>
  );
}

interface BannerPositionMenuProps {
  collection: Collection;

  menuState: MenuState;

  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
}

const BannerPositionMenu: React.FC<BannerPositionMenuProps> = ({ collection, menuState, onMenuChange }) => {
  const collectionContext = useContext(CollectionContext);
  const positionValue = _.get(collection, 'banner.position');

  const persistedCollection = useSelector((state: any) => {
    return _.get(state, `${collectionContext.itemSelector}`);
  });

  const handlePositionChange = (position: string) => {
    collectionContext.updateCollectionProperty('banner', {
      ...collection.banner,
      position: position
    });
  }

  const handleConfirm = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: null
    });
  }

  const handleCancel = () => {
    collectionContext.updateCollectionProperty('banner', {
      ...collection.banner,
      position: persistedCollection.banner?.position
    });

    onMenuChange({
      ...menuState,
      secondaryAction: null
    });
  }

  return (
    <Actions.Container>
      <Actions.Row className={styles.positionActionRow}>
        <Actions.Item className={styles.positions}>
          {POSITIONS.map((position) => {
            const active = position.value === positionValue;
            return (
              <div key={position.value}
                className={cx(styles.position, {
                  [styles.active]: active
                })}
                onClick={() => handlePositionChange(position.value)}
              />
            );
          })}
        </Actions.Item>
        <Actions.Button width={3} icon="paperflite-tick" className={styles.primaryAction} title="Save" onClick={handleConfirm} />
        <Actions.Button width={3} icon="paperflite-close" title="Cancel" onClick={handleCancel} />
      </Actions.Row>
    </Actions.Container>
  );
}

interface SharedByEditMenuProps {
  collection: Collection;
  updating: boolean;
}

const SharedByEditMenu: React.FC<SharedByEditMenuProps> = ({ collection, updating }) => {
  const dispatch = useDispatch();
  const { sectionId: currentSectionIndex } = useParams();

  const conversation = useSelector((state: any) => {
    return _.get(state, "conversations.edit", false);  
  });
  const isConversation = !!conversation;

  const user = useSelector((state: any) => state.users?.me);
  const collectionContext = useContext(CollectionContext);
  const editCollection = useSelector((state: any) => {
    return _.get(state, `${collectionContext.editSelector}`);
  });

  const assets = _.get(editCollection, "entities", []);
  const sectionItems = _.get(editCollection, "sections", []);
  const hasSections = _.size(sectionItems) > 0;

  const contactCardExists = useMemo(() => {
    if (hasSections && currentSectionIndex) {
      return getContactCardExists(_.get(sectionItems, `[${currentSectionIndex}].item.entities`, []));
    }
    
    return getContactCardExists(assets);
  }, [editCollection, hasSections, currentSectionIndex]);

  const addContactCard = () => {
    if (contactCardExists) return;

    if (isConversation) {
      dispatch(addAssetToConversation({
        conversation: _.get(conversation, "id", ""),
        section: currentSectionIndex ? parseInt(currentSectionIndex) : undefined,
        assets: [generateDefaultContactCard()],
        addAt: "start"
      }));

      return;
    }
    
    dispatch(addAssetToCollection({
      collection: _.get(collection, "id", ""),
      section: currentSectionIndex ? parseInt(currentSectionIndex) : undefined,
      assets: [generateDefaultContactCard()],
      addAt: "start"
    }));
  }

  return (
    <Actions.Item className={cx(styles.sharedByMenu, {
      [styles.disabled]: !contactCardExists,
      [styles.updating]: updating,
    })}>
      <div className={styles.userIcon}>
        <Thumbnail media={_.get(user, "icon") || DefaultThumbnail} width={100} height={100} />
      </div>
      <i
        className={cx("paperflite-add", styles.addContactCard, {
          [styles.disabled]: contactCardExists,
        })}
        onClick={addContactCard}
      />
    </Actions.Item>
  );
}

export { Menubar, type MenuState };